import {publishOpenCopilotChat} from '@github-ui/copilot-chat-events'
import {CopilotChatIntents} from '@github-ui/copilot-chat-types'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useCallback} from 'react'

export interface AskCopilotPrompt {
  /** Short label shown in the menu. */
  label: string
  /** Full prompt sent to Copilot Chat. */
  prompt: string
}

type AskCopilotLocation = 'readme-header' | 'no-readme-card'

const ASK_COPILOT_PROMPTS: AskCopilotPrompt[] = [
  {label: 'Give me a high-level overview', prompt: 'Give me a high-level overview of this repository.'},
  {label: 'How can I contribute?', prompt: 'How can I contribute to this repository?'},
]

export const ASK_COPILOT_README_PROMPTS: AskCopilotPrompt[] = [
  ...ASK_COPILOT_PROMPTS,
  {label: 'Summarize latest changes', prompt: 'Summarize the latest changes in this repository.'},
]

export const ASK_COPILOT_NO_README_PROMPTS: AskCopilotPrompt[] = [
  ...ASK_COPILOT_PROMPTS,
  {label: 'Generate a README', prompt: 'Generate a README for this repository.'},
]

export function sendSuggestionsShown(location: AskCopilotLocation) {
  sendEvent('copilot_generated_suggestions_shown', {
    contextType: location,
    referenceGroup: 'repo-overview',
  })
}

/**
 * Business logic for the Ask Copilot surfaces. Selecting a prompt opens Contextual Chat
 * seeded with the prompt and fires a Copilot chat-open telemetry event. Kept separate from the
 * presentation so each consumer (sidebar header icon, no-readme card) stays a thin view layer.
 */
export function useAskCopilotPrompts({location, readme}: {location: AskCopilotLocation; readme: boolean}) {
  const onPromptClick = useCallback(
    (prompt: AskCopilotPrompt) => {
      const id = location
      publishOpenCopilotChat({
        intent: CopilotChatIntents.conversation,
        content: prompt.prompt,
        id,
        source: 'external',
      })
      sendEvent('copilot_generated_suggestion_click', {
        content: prompt.label,
        contextType: id,
        referenceGroup: 'repo-overview',
      })
    },
    [location],
  )

  return {prompts: readme ? ASK_COPILOT_README_PROMPTS : ASK_COPILOT_NO_README_PROMPTS, onPromptClick}
}
